import React, { useEffect, useMemo, useState } from 'react';
import { Form, DatePicker, Radio, Checkbox } from 'antd';
import {
  CustomUploadPhoto,
  FormInput,
  IconSmallArrow,
  OrangeButton,
  IconCalendar,
  TooltipButton,
  IconClipboardType,
  IconInfo,
  FileUpload,
  SelectOption,
  IconSoberLiving,
  IconChild,
  IconNoRelation,
} from 'components';
import classNames from 'classnames';
import { COLORS } from 'utils/colors';
import dayjs from 'dayjs';
import { getRequestData, updateClient, updateUserImage, uploadDocument, deleteDocument } from 'api/api';
import { endpoints } from 'api/endpoints';
import { useLocation, useNavigate } from 'react-router-dom';
import { DATE_FORMAT, ROLE_TYPES, SLH_TYPES } from 'api/constants';
import {
  blockInvalidChar,
  formatDateAsUtcStartOfDay,
  getBackendCalendarDayjs,
  getIdFromPath,
} from 'utils/helpers';

const defaultFormData = {
  profile: {
    avatar: '',
    firstname: '',
    lastname: '',
    email: '',
    gender: '',
    phone: '',
    country_code: '+1',
    birthdate: '',
    identifyas: '',
    documents: [],
    startDate: '',
    moveInDate: '',
    slh: '',
    tasks: [],
  },
};

export const EditClientByManager = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [avatarImg, setAvatarImg] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState([]);
  const [removedDocIds, setRemovedDocIds] = useState([]);
  const [isMoveInTheSame, setIsMoveInTheSame] = useState(true);
  const [partner, setPartner] = useState(null);
  const [form] = Form.useForm();

  const [formData, setFormData] = useState(defaultFormData);

  useEffect(() => {
    const userID = getIdFromPath(location.pathname);
    (async () => {
      const { data } = await getRequestData(endpoints.getUser(userID), ROLE_TYPES.slh_manager);

      if (data) {
        const updatedFormData = {
          profile: {
            ...data.profile,
            avatar: data.profile.avatar,
            firstname: data.profile.firstname,
            lastname: data.profile.lastname,
            email: data.profile.email,
            gender: data.profile.gender,
            phone: data.profile.phone,
            country_code: data.profile.country_code ?? '+1',
            identifyas: data.profile.identifyas,
            birthdate: data.profile.birthdate,
            documents: data.client.documents,
            startDate: data.client.start_date,
            moveInDate: data.client.move_in_date,
            slh: data.client.name,
            tasks: data.client.tasks,
            forms: data.client.forms,
          },
        };
        setFormData(updatedFormData);
        setIsMoveInTheSame(
          updatedFormData.profile?.moveInDate === updatedFormData.profile?.startDate,
        );
        form.setFieldsValue(updatedFormData);
        // fetch partner details to get fiscal_start_dow
        try {
          if (data.client?.partner_id) {
            const partnerResp = await getRequestData(
              endpoints.getPartner(data.client.partner_id),
              ROLE_TYPES.slh_manager,
            );
            if (partnerResp && partnerResp.data) {
              setPartner(partnerResp.data);
            }
          }
        } catch (e) {
          // ignore partner fetch errors
        }
      }
    })();
  }, [form, location.pathname]);

  const disabledStartDateEdit = (current) => {
    if (!current) return false;
    const lastInvoiceDate = partner?.submittedLastInvoiceEndDate;
    if (lastInvoiceDate) {
      return current.startOf('day').valueOf() <= dayjs(lastInvoiceDate).startOf('day').valueOf();
    }

    const sixWeeksAgo = dayjs().subtract(6, 'week').startOf('day');
    return current.startOf('day').isBefore(sixWeeksAgo);
  };

  const canEditStartDate = useMemo(() => {
    const startDate = formData.profile.startDate;
    if (!startDate) return false;

    const lastInvoiceDate = partner?.submittedLastInvoiceEndDate;
    if (lastInvoiceDate) {
      return dayjs(startDate).startOf('day').isAfter(dayjs(lastInvoiceDate).startOf('day'));
    }

    const sixWeeksAgo = dayjs().subtract(6, 'week').startOf('day');
    return !dayjs(startDate).startOf('day').isBefore(sixWeeksAgo);
  }, [formData.profile.startDate, partner]);

  const handleImageUpload = (file) => {
    setAvatarImg(file);
  };

  const handleGenderChange = (e) => {
    const shortGender = e.target.value?.slice(0, 1).toUpperCase();

    setFormData({
      ...formData,
      profile: {
        ...formData.profile,
        gender: shortGender,
      },
    });
  };

  const handleDateOfBornChange = (date) => {
    const birthdate = formatDateAsUtcStartOfDay(date);

    setFormData({
      ...formData,
      profile: {
        ...formData.profile,
        birthdate,
      },
    });
  };

  const handleDocSelect = (files) => {
    setSelectedDoc(files || []);
  };

  const handleDocRemove = (file) => {
    const docId = file.id ?? file.uid;
    if (docId) {
      setRemovedDocIds((prev) => [...prev, docId]);
    }
  };

  const handleMoveInTheSameChange = (e) => {
    const isChecked = e.target.checked;
    setIsMoveInTheSame(isChecked);

    setFormData({
      ...formData,
      profile: {
        ...formData.profile,
        moveInDate: isChecked ? formData.profile.startDate : formData.profile.moveInDate,
      },
    });
  };

  const handleMoveInDateChange = (date) => {
    const moveInDate = formatDateAsUtcStartOfDay(date);
    setIsMoveInTheSame(moveInDate === formData.profile.startDate);
    setFormData({
      ...formData,
      profile: {
        ...formData.profile,
        moveInDate,
      },
    });
  };

  const handleStartDateChange = (date) => {
    const startDate = formatDateAsUtcStartOfDay(date);

    let newMoveInDate = formData.profile.moveInDate;
    let newIsMoveInSame = isMoveInTheSame;

    if (isMoveInTheSame) {
      newMoveInDate = startDate;
    } else if (formData.profile.moveInDate) {
      if (dayjs(startDate).isBefore(dayjs(formData.profile.moveInDate), 'day')) {
        newMoveInDate = startDate;
        newIsMoveInSame = true;
      }
    }

    setIsMoveInTheSame(newIsMoveInSame);

    setFormData({
      ...formData,
      profile: {
        ...formData.profile,
        startDate,
        moveInDate: newMoveInDate,
      },
    });
  };

  const handleFormSubmit = (values) => {
    const userID = formData.profile.user_id;

    values = {
      ...values,
      profile: {
        ...values.profile,
        gender: formData.profile.gender,
        birthdate: formData.profile.birthdate,
        moveInDate: isMoveInTheSame ? formData.profile.startDate : formData.profile.moveInDate,
        startDate: formData.profile.startDate,
      },
    };

    updateClient(values, userID, ROLE_TYPES.slh_manager, () => {
      avatarImg && updateUserImage(userID, avatarImg, ROLE_TYPES.slh_manager);
      removedDocIds.forEach((docId) => deleteDocument(userID, docId, ROLE_TYPES.slh_manager));
      selectedDoc.forEach((doc) => {
        uploadDocument(userID, doc, ROLE_TYPES.slh_manager);
      });
      setTimeout(() => {
        navigate('/clients');
      }, 100);
    });
  };

  const onPastePhone = (e) => {
    e.preventDefault();

    const pastedText = e.clipboardData.getData('text');

    const digitsOnly = pastedText.replace(/\D/g, '');

    const prefixDigits = (formData.profile?.country_code || '+1').replace(/\D/g, '');

    const sanitizedPaste = digitsOnly.startsWith(prefixDigits)
      ? digitsOnly.slice(prefixDigits.length)
      : digitsOnly;

    const currentPhone = form.getFieldValue(['profile', 'phone']) || '';

    const nextValue = `${currentPhone}${sanitizedPaste}`;

    form.setFieldsValue({
      profile: {
        ...form.getFieldValue('profile'),
        phone: nextValue,
      },
    });
  };

  const sanitizePhoneValue = (rawValue, prefix) => {
    const digitsOnly = rawValue.replace(/\D/g, '');
    const prefixDigits = prefix.replace(/\D/g, '');

    return digitsOnly.startsWith(prefixDigits) ? digitsOnly.slice(prefixDigits.length) : digitsOnly;
  };

  const onPhoneChange = (e) => {
    const input = e.target;
    const rawValue = input.value;

    const sanitized = sanitizePhoneValue(rawValue, formData.profile?.country_code || '+1');

    if (rawValue !== sanitized) {
      form.setFieldsValue({
        profile: {
          ...form.getFieldValue('profile'),
          phone: sanitized,
        },
      });
    }
  };

  return (
    formData.profile.firstname && (
      <Form
        form={form}
        name="edit-client-by-manager"
        className="flex flex-col w-full gap-6"
        initialValues={formData}
        onFinish={handleFormSubmit}
        requiredMark={false}
      >
        <div className="flex justify-between w-full">
          <div className="flex items-center gap-x-4">
            <div
              onClick={() => navigate(-1)}
              className="w-10 h-10 bg-white rounded-lg shadow-block-shadow flex items-center justify-center cursor-pointer"
            >
              <IconSmallArrow className="w-2 h-4" />
            </div>
            <p className="nunito-title">Edit Resident</p>
          </div>
          <OrangeButton type="submit" label="Save" />
        </div>
        <div className="flex gap-6">
          <div className="flex flex-col gap-[18px] base-block pt-[30px] w-[442px]">
            <p className="nunito-title px-[30px] pb-[11px]">Resident Information</p>
            <div
              className={classNames(
                'relative w-full flex flex-col pt-14 pb-[30px] gap-y-[18px] border-t border-color-lightBlue px-[30px] self-center items-center',
                'before:content-[""] before:absolute before:w-4 before:h-4 before:rounded-full before:border before:border-color-lightBlue before:bg-white before:z-10 before:top-0 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
              )}
            >
              <CustomUploadPhoto
                avatar={formData.profile.avatar}
                className="self-center"
                onImageUpload={handleImageUpload}
              />
              <div className="flex gap-x-[18px]">
                <FormInput
                  label="First Name"
                  name={['profile', 'firstname']}
                  placeholder="First Name"
                />
                <FormInput
                  label="Last Name"
                  name={['profile', 'lastname']}
                  placeholder="Last Name"
                />
              </div>
              <Form.Item className="flex flex-col w-full">
                <p className="text-14 font-medium mb-2">Date of Birth</p>
                <DatePicker
                  allowClear={false}
                  onChange={(_, date) => handleDateOfBornChange(date)}
                  format={DATE_FORMAT}
                  placeholder="Pick Date"
                  className="h-[44px] text-base font-medium base-block rounded-lg w-full"
                  defaultValue={getBackendCalendarDayjs(formData.profile.birthdate)}
                  suffixIcon={<IconCalendar color={COLORS.blue} />}
                />
              </Form.Item>
              <FormInput className="w-full" label="Email" value={formData.profile.email} disabled />
              <div className="flex gap-x-[18px]">
                <FormInput
                  type="password"
                  label="Password"
                  placeholder="Password"
                  value="123456789"
                  visibilityToggle={false}
                  disabled
                />
                <FormInput
                  type="password"
                  label="Confirm Password"
                  placeholder="Password"
                  value="123456789"
                  visibilityToggle={false}
                  disabled
                />
              </div>
              <Form.Item className="flex flex-col w-full">
                <p className="text-14 font-medium mb-2">Gender</p>
                <Radio.Group
                  onChange={handleGenderChange}
                  name={['profile', 'gender']}
                  className="gap-y-4"
                  defaultValue={formData.profile.gender}
                >
                  <Radio
                    className="px-4 py-2.5 bg-white border border-color-divider rounded-xl"
                    value={'M'}
                    disabled={
                      !!partner?.gender &&
                      !partner.gender.split(',').map((g) => g.trim()).includes('M')
                    }
                  >
                    Male
                  </Radio>
                  <Radio
                    className="px-4 py-2.5 bg-white border border-color-divider rounded-xl"
                    value={'F'}
                    disabled={
                      !!partner?.gender &&
                      !partner.gender.split(',').map((g) => g.trim()).includes('F')
                    }
                  >
                    Female
                  </Radio>
                </Radio.Group>
              </Form.Item>
              <FormInput
                label="Phone"
                placeholder="Phone"
                className="w-full"
                name={['profile', 'phone']}
                type="text"
                pattern="['0-9']*"
                maxLength={10}
                onChange={onPhoneChange}
                onWheel={(e) => {
                  e.target.blur();
                  e.stopPropagation();

                  setTimeout(() => {
                    e.target.focus();
                  }, 0);
                }}
                onKeyDown={blockInvalidChar}
                onPaste={onPastePhone}
                inputClassName="[&>*]:cursor-default"
                prefix={formData.profile?.country_code}
              />
              <div className="flex flex-col gap-y-1 w-full">
                <p className="text-14 font-medium">Document</p>
                <FileUpload
                  className="w-full"
                  onFileSelect={handleDocSelect}
                  files={formData.profile?.documents}
                  onFileRemove={handleDocRemove}
                />
              </div>
              <div className="flex flex-col w-full gap-y-1">
                <p className="text-14 font-medium">SLH</p>
                <SelectOption
                  dropdownStyle={{ padding: '16px', background: 'white' }}
                  disabled
                  suffixIcon={
                    <IconSmallArrow color={COLORS.darkGrey} className="rotate-[-90deg] w-3 h-3" />
                  }
                  className="h-[44px] text-base font-medium base-block rounded-lg placeholder:!text-darkGrey"
                  placeholder={
                    <div className="flex items-center justify-start px-2 py-1 gap-x-1 text-black rounded-md capitalize duration-150 bg-white hover:bg-orange">
                      {formData.profile.slh.type === SLH_TYPES.parent ? (
                        <IconSoberLiving className="w-4 h-4" />
                      ) : formData.profile.slh.type === SLH_TYPES.child ? (
                        <IconChild className="w-4 h-4" />
                      ) : (
                        <IconNoRelation className="w-4 h-4" />
                      )}
                      <p className="font-14 flex-1 text-ellipsis overflow-hidden">
                        {formData.profile.slh}
                      </p>
                    </div>
                  }
                />
                <div className="flex justify-between mt-1">
                  <Checkbox className="flex items-center !bg-transparent" checked disabled>
                    <p className="text-16 ml-2.5 text-darkGrey">Accepted at SLH*</p>
                  </Checkbox>
                  <TooltipButton
                    icon={<IconInfo color={COLORS.blue} />}
                    text="Info"
                    tooltipText="Residents must have already applied for entry to a partner SLH and been accepted for a term of residency at the SLH."
                  />
                </div>
              </div>
              <Form.Item className="flex flex-col w-full">
                <p className="text-14 font-medium mb-2">Scholarship Start Date</p>
                <DatePicker
                  allowClear={false}
                  format={DATE_FORMAT}
                  placeholder="Pick Date"
                  className="h-[44px] text-base font-medium base-block rounded-lg w-full"
                  value={getBackendCalendarDayjs(formData.profile.startDate) || undefined}
                  suffixIcon={<IconCalendar color={COLORS.darkGrey} />}
                  disabled={!canEditStartDate}
                  onChange={(_, date) => handleStartDateChange(date)}
                  disabledDate={disabledStartDateEdit}
                />
              </Form.Item>
              <div className="flex w-full">
                <div className="flex flex-col w-full gap-y-1">
                  <p className="text-14 font-medium">Move-in Date*</p>
                  <div className="flex w-full gap-x-3">
                    <DatePicker
                      allowClear={false}  
                      onChange={(_, date) => handleMoveInDateChange(date)}
                      format={DATE_FORMAT}
                      placeholder="Pick Date"
                      disabled={isMoveInTheSame}
                      className="h-[44px] text-base font-medium base-block rounded-lg"
                      name={['profile', 'moveInDate']}
                      value={getBackendCalendarDayjs(formData.profile.moveInDate)}
                      suffixIcon={<IconCalendar color={COLORS.blue} />}
                    />
                    <Checkbox
                      onChange={handleMoveInTheSameChange}
                      name="same-as-start-date"
                      className="flex items-center !bg-transparent flex-1"
                      checked={isMoveInTheSame}
                    >
                      <p className="text-16 ml-2.5">Same as start</p>
                    </Checkbox>
                  </div>
                </div>
              </div>
              <FormInput
                className="w-full"
                label="Identify as"
                name={['profile', 'identifyas']}
                placeholder="Identify as"
              />
            </div>
          </div>
          <div className="flex flex-col base-block pt-[30px] flex-1">
            <div className="flex justify-between px-[30px] pb-[17px]">
              <p className="nunito-title">FORMS</p>
              {/* <Input
              className='search w-[300px] text-lightBlue text-16 rounded-ten bg-light'
              type='text'
              placeholder="Search"
              addonBefore={<IconSearch color={COLORS.lightBlue} />}
            // onSearch={onSearch}
            /> */}
            </div>
            {formData.profile.forms.length > 0 && (
              <div
                className={classNames(
                  'relative w-full flex flex-col pt-6 pb-[30px] border-t border-color-lightBlue px-[30px] self-center items-center',
                  'before:content-[""] before:absolute before:w-4 before:h-4 before:rounded-full before:border before:border-color-lightBlue before:bg-white before:z-10 before:top-0 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
                )}
              >
                <div className="flex flex-col w-full">
                  <p className="text-14 mb-1">Programs Participations</p>
                  {formData.profile.forms.map(({ name, id }) => {
                    return (
                      <div
                        className="flex justify-between items-center bg-white shadow-block-shadow rounded-lg w-full p-4 gap-x-4 mb-6"
                        key={id}
                      >
                        <Checkbox
                          name="weekly-form"
                          className="weekly-forms !bg-transparent"
                          checked
                          disabled
                        >
                          <div className="flex items-center">
                            <IconClipboardType className="ml-2.5 mr-1 min-w-6 min-h-6" />
                            <p className="text-16 text-black">{name}</p>
                          </div>
                        </Checkbox>
                        <TooltipButton
                          icon={<IconInfo color={COLORS.blue} />}
                          text="Info"
                          tooltipText="The resident will get changed Weekly Summary Form that will cover also DOER related questions."
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </Form>
    )
  );
};
